import { createServerFn } from '@tanstack/react-start';
import { eq } from 'drizzle-orm';
import { z } from 'zod';
import { deleteJob as deleteJobRecord, getJob } from '@/db/models';
import { applications, jobs } from '@/db/schema';
import { getDb } from '@/lib/db';
import { authMiddleware } from '@/lib/server-fn-auth';
import { queryJobsPage } from '@/server/jobs.queries';
import { rowIdSchema } from '@/server/validators';

/**
 * Paginated job feed. Mirrors the legacy `GET /api/jobs` handler: excludes
 * skip-scored jobs, left-joins the application row, orders by score then
 * recency. Query logic lives in `queryJobsPage` so it stays unit-testable.
 */
export const fetchJobs = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(
    z.object({
      minScore: z.number().int().optional(),
      page: z.number().int().min(1).default(1),
      pageSize: z.number().int().min(1).max(200).default(50),
      source: z.string().optional(),
      status: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => queryJobsPage(getDb(), data));

/** Single job with its joined application detail. Mirrors `GET /api/jobs/$id`. */
export const fetchJob = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(z.object({ id: rowIdSchema }))
  .handler(async ({ data }) => {
    const db = getDb();
    const [row] = await db
      .select({
        applicationId: applications.id,
        appStatus: applications.status,
        company: jobs.company,
        coverLetter: applications.cover_letter,
        coverLetterPdfPath: applications.cover_letter_pdf_path,
        description: jobs.description,
        descriptionMd: jobs.description_md,
        externalId: jobs.external_id,
        id: jobs.id,
        injectionEvidence: jobs.injection_evidence,
        injectionSuspected: jobs.injection_suspected,
        location: jobs.location,
        matchReasoning: jobs.match_reasoning,
        matchScore: jobs.match_score,
        postedAt: jobs.posted_at,
        resumePdfPath: applications.resume_pdf_path,
        salaryMax: jobs.salary_max,
        salaryMin: jobs.salary_min,
        scrapedAt: jobs.scraped_at,
        source: jobs.source,
        status: jobs.status,
        tailoredResumeMd: applications.tailored_resume_md,
        techStack: jobs.tech_stack,
        title: jobs.title,
        url: jobs.url,
      })
      .from(jobs)
      .leftJoin(applications, eq(applications.job_id, jobs.id))
      .where(eq(jobs.id, data.id))
      .limit(1);

    if (!row) {
      throw new Error('Not found');
    }

    return row;
  });

/** Delete a job and its applications. Mirrors `POST /api/jobs/$id/delete`. */
export const deleteJob = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(z.object({ id: rowIdSchema }))
  .handler(async ({ data }) => {
    const db = getDb();
    const job = await getJob(db, data.id);

    if (!job) {
      throw new Error('Job not found');
    }

    await deleteJobRecord(db, data.id);

    return { deleted: true as const };
  });

/** Requeue score-failed jobs back to `new`. Mirrors `POST /api/jobs/retry-failed`. */
export const retryFailed = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = await getDb()
      .update(jobs)
      .set({ status: 'new' })
      .where(eq(jobs.status, 'score_failed'));

    return { requeued: result.rowCount ?? 0 };
  });
